import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import axios from 'axios'
import useIsMounted from '../../hooks/useIsMounted'
import apiConfig from '../../config/api-config'
import localConfig from '../../config/local-config'
import nrLogger from '../helpers/nrLogger'

export const QuestionAnswerContext = createContext()
export default function QuestionAnswerProvider({
  children, questions, products, canonicalUri,
}) {
  const isMounted = useIsMounted()
  const isDataPresent = Boolean(questions)
  const parseProductItem = data => {
    let skuTitle = ''
    if (data.brand) {
      skuTitle += `${data.brand} `
    }
    skuTitle += data.skuTitle
    return {
      skuTitle,
      price: data.pricing.regularPrice,
      productUrl: data.productUri,
      imageUrl: data.productImageUrl,
    }
  }

  const parseQuestionData = useCallback((nonBotData = []) => {
    if (!isDataPresent && nonBotData.length === 0) {
      return []
    }
    const questionData = nonBotData.length > 0 ? nonBotData : questions
    const questionAnswerData = []
    questionData.forEach(record => {
      const pid = record.id
      // Find index of item
      const productIndex = products.items.findIndex(item => item.id === pid)
      if (productIndex >= 0) {
        questionAnswerData.push({
          ...record,
          ...parseProductItem(products.items[productIndex]),
        })
      }
    })
    return {
      data: {
        questionAnswers: questionAnswerData,
      },
    }
  }, [isDataPresent, products, questions])
  const [questionsAnswers, setQuestionsAnswers] = useState(parseQuestionData())
  const baseUrl = apiConfig.apiURL
  const contextValue = useMemo(() => ({
    questionsAnswers,
    setQuestionsAnswers,
  }), [
    questionsAnswers,
    setQuestionsAnswers,
  ])

  const getLastThreeProducts = useCallback(() => {
    const pid = []
    const productUri = []
    products.items.slice(-3).forEach(data => {
      pid.push(data.id)
      productUri.push(`/${data.sku}`)
    })

    return {
      pid: JSON.stringify(pid),
      productUri: JSON.stringify(productUri),
    }
  }, [products])

  useEffect(() => {
    if (isDataPresent || !products) {
      return
    }
    const uri = canonicalUri || window.location.pathname
    const apiUrl = `${baseUrl}/v1/question-answers`
    const params = {
      domain: localConfig.hostname,
      uri,
      ...getLastThreeProducts(),
    }
    axios.get(apiUrl, {
      params,
      headers: {
        apikey: apiConfig.apiKey,
      },
    }).then(result => {
      if (isMounted()) {
        const returnData = parseQuestionData(result.data.data?.questionAnswers)
        setQuestionsAnswers(returnData)
      }
    }).catch(err => {
      nrLogger.error('Error fetching Popular Products', err)
    })
  }, [
    baseUrl,
    canonicalUri,
    getLastThreeProducts,
    isDataPresent,
    isMounted,
    parseQuestionData,
    products,
  ])

  return (
    <QuestionAnswerContext.Provider value={contextValue}>
      { children }
    </QuestionAnswerContext.Provider>
  )
}
